import { api, type Board, type BoardEntry } from '../net/api';
import type { RankedMode } from '../sim/types';
import { store } from '../store';
import { $, el, fmt } from './dom';

type Period = 'day' | 'week' | 'all';

function whenText(ms: number): string {
  const d = new Date(ms);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'hoy';
  return d.toLocaleDateString('es', { day: 'numeric', month: 'short' });
}

function rowEl(e: BoardEntry, me: boolean, medal: boolean): HTMLLIElement {
  return el('li', { class: me ? 'me' : '' }, [
    el('span', { class: medal && e.rank <= 3 ? 'rank medal' : 'rank', text: String(e.rank) }),
    el('span', { class: 'name', text: e.nickname }, [
      el('span', { class: 'meta', text: `${fmt(e.jumps)} saltos · combo ${e.bestCombo} · ${whenText(e.createdAt)}${me ? ' · tú' : ''}` }),
    ]),
    el('span', { class: 'pts', text: fmt(e.score) }),
  ]);
}

/** Fills a list with the top entries and, if needed, the player's own row. */
export function renderBoard(list: HTMLElement, board: Board, myId?: number): void {
  const items: HTMLElement[] = board.entries.map((e) => rowEl(e, e.id === myId, true));
  if (board.me && !board.entries.some((e) => e.id === board.me!.id)) {
    items.push(el('li', { class: 'board-gap', text: '…', attrs: { 'aria-hidden': 'true' } }));
    items.push(rowEl(board.me, true, false));
  }
  list.replaceChildren(...items);
}

export class BoardScreen {
  private mode: RankedMode = 'arcade';
  private period: Period = 'all';
  private body = $('boardBody');
  private periodTabs = $('periodTabs');
  private req = 0;

  constructor() {
    document.querySelectorAll<HTMLButtonElement>('[data-board-mode]').forEach((b) =>
      b.addEventListener('click', () => this.open(b.dataset.boardMode as RankedMode)),
    );
    document.querySelectorAll<HTMLButtonElement>('[data-board-period]').forEach((b) =>
      b.addEventListener('click', () => {
        this.period = b.dataset.boardPeriod as Period;
        void this.load();
      }),
    );
  }

  open(mode: RankedMode = this.mode): void {
    this.mode = mode;
    void this.load();
  }

  private syncTabs(): void {
    document.querySelectorAll<HTMLButtonElement>('[data-board-mode]').forEach((b) =>
      b.setAttribute('aria-selected', String(b.dataset.boardMode === this.mode)),
    );
    document.querySelectorAll<HTMLButtonElement>('[data-board-period]').forEach((b) =>
      b.setAttribute('aria-selected', String(b.dataset.boardPeriod === this.period)),
    );
    this.periodTabs.hidden = this.mode === 'daily';
  }

  async load(): Promise<void> {
    this.syncTabs();
    const id = ++this.req;
    this.body.replaceChildren(el('p', { class: 'empty', text: 'Cargando la tabla…' }));
    const mine = store.myScore(this.mode);
    try {
      const board = await api.leaderboard(this.mode, this.mode === 'daily' ? 'day' : this.period, mine?.id);
      if (id !== this.req) return;
      if (!board.entries.length) {
        const msg = this.mode === 'daily'
          ? 'Nadie ha jugado el reto de hoy todavía. ¡Puedes ser el primero!'
          : 'Todavía no hay récords en este periodo. ¡Juega y estrena la tabla!';
        this.body.replaceChildren(el('p', { class: 'empty', text: msg }));
        return;
      }
      const list = el('ol', { class: 'board' });
      renderBoard(list, board, mine?.id);
      const foot = el('p', {
        class: 'sub',
        text: `${fmt(board.total)} ${board.total === 1 ? 'récord' : 'récords'}${this.mode === 'daily' ? ` el ${board.day}` : ''}.`,
      });
      this.body.replaceChildren(list, foot);
    } catch {
      if (id !== this.req) return;
      this.body.replaceChildren(
        el('p', { class: 'empty', text: 'No se pudo cargar la tabla. Revisa tu conexión e inténtalo otra vez.' }),
      );
    }
  }
}
